import { useEffect, useState } from 'react';
import AdminTopbar from '../AdminTopbar';
import { getSubmissions, updateSubmissionStatus } from '../../firebase/submissions';

/* ── PDF export ── */
function exportPDF(sub) {
  const fmt = (val) => {
    if (!val) return '—';
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    return String(val);
  };
  const fmtTs = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-CA', { dateStyle: 'long', timeStyle: 'short' });
  };

  const sections = [
    { heading: 'Business', fields: [['Business name','bizname'],['Industry','industry'],['Website','website'],['City / Region','city'],['Country','country'],['Phone','phone'],['Description','bizdes']] },
    { heading: 'AI Agent Branding', fields: [['Agent name','agentname'],['Voice gender','voice'],['Tone','tone'],['Brand colour','color'],['Subdomain','domain'],['Logo link','logo'],['Opening message','firstmsg']] },
    { heading: 'Channels & Booking', fields: [['Active channels','channels'],['Booking system','booking'],['Calendar system','cal_other'],['Calendar email','cal_email'],['Business hours','hours'],['Appt duration','appt_dur']] },
    { heading: 'Knowledge Base', fields: [['Services & pricing','services'],['Top FAQs','faqs'],['AI must never','never'],['Document links','docs'],['Languages','languages'],['Other languages','lang_other']] },
    { heading: 'Integrations', fields: [['CRM','crm'],['Payments','payments'],['E-commerce','ecom'],['Other tools','other_tools'],['Lead notify','notify']] },
    { heading: 'Contact', fields: [['Full name','name'],['Role','role'],['Email','email'],['Mobile','mobile'],['Availability','availability'],['Plan','plan'],['How heard','source'],['Notes','notes']] },
  ];

  const rows = sections.map(sec => {
    const fields = sec.fields
      .map(([label, key]) => {
        const v = fmt(sub[key]);
        if (v === '—') return '';
        return `
          <tr>
            <td class="key">${label}</td>
            <td class="val">${v.replace(/\n/g, '<br/>')}</td>
          </tr>`;
      })
      .filter(Boolean)
      .join('');
    if (!fields) return '';
    return `
      <tr class="section-head"><td colspan="2">${sec.heading}</td></tr>
      ${fields}`;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Setup Form — ${fmt(sub.bizname)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 12px; color: #111; background: #fff; padding: 32px 40px; }
    .header { margin-bottom: 24px; border-bottom: 2px solid #111; padding-bottom: 14px; }
    .header h1 { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
    .header p { font-size: 11px; color: #555; }
    .status { display: inline-block; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; padding: 2px 8px; border-radius: 4px; margin-top: 6px; background: #f0f0f0; color: #333; }
    table { width: 100%; border-collapse: collapse; }
    tr.section-head td { background: #f5f5f5; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #444; padding: 7px 10px; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; }
    td.key { width: 34%; padding: 7px 10px; color: #555; vertical-align: top; border-bottom: 1px solid #eee; }
    td.val { padding: 7px 10px; color: #111; vertical-align: top; border-bottom: 1px solid #eee; line-height: 1.55; }
    .footer { margin-top: 24px; font-size: 10px; color: #aaa; border-top: 1px solid #eee; padding-top: 10px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${fmt(sub.bizname)} — Client Setup Form</h1>
    <p>Submitted: ${fmtTs(sub.submittedAt)}</p>
    <span class="status">${(sub.status || 'new').replace('-', ' ')}</span>
  </div>
  <table>${rows}</table>
  <div class="footer">Klaivox · klaivox.com · Generated ${new Date().toLocaleString('en-CA')}</div>
  <script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

/* ── helpers ── */
const fmt = (val) => {
  if (!val) return '—';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  return String(val);
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-CA', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const STATUS_OPTIONS = ['new', 'in-progress', 'done'];
const STATUS_LABEL = { new: 'New', 'in-progress': 'In Progress', done: 'Done' };
const STATUS_COLOR = { new: 'amber', 'in-progress': 'blue', done: 'green' };

/* ── full detail sections ── */
const DETAIL_SECTIONS = [
  {
    heading: 'Business',
    fields: [
      ['Business name', 'bizname'],
      ['Industry',      'industry'],
      ['Website',       'website'],
      ['City / Region', 'city'],
      ['Country',       'country'],
      ['Phone',         'phone'],
      ['Description',   'bizdes'],
    ],
  },
  {
    heading: 'AI Agent Branding',
    fields: [
      ['Agent name',       'agentname'],
      ['Voice gender',     'voice'],
      ['Tone',             'tone'],
      ['Brand colour',     'color'],
      ['Subdomain',        'domain'],
      ['Logo link',        'logo'],
      ['Opening message',  'firstmsg'],
    ],
  },
  {
    heading: 'Channels & Booking',
    fields: [
      ['Active channels',    'channels'],
      ['Booking system',     'booking'],
      ['Calendar system',    'cal_other'],
      ['Calendar email',     'cal_email'],
      ['Business hours',     'hours'],
      ['Appt duration',      'appt_dur'],
    ],
  },
  {
    heading: 'Knowledge Base',
    fields: [
      ['Services & pricing', 'services'],
      ['Top FAQs',           'faqs'],
      ['AI must never',      'never'],
      ['Document links',     'docs'],
      ['Languages',          'languages'],
      ['Other languages',    'lang_other'],
    ],
  },
  {
    heading: 'Integrations',
    fields: [
      ['CRM',           'crm'],
      ['Payments',      'payments'],
      ['E-commerce',    'ecom'],
      ['Other tools',   'other_tools'],
      ['Lead notify',   'notify'],
    ],
  },
  {
    heading: 'Contact',
    fields: [
      ['Full name',     'name'],
      ['Role',          'role'],
      ['Email',         'email'],
      ['Mobile',        'mobile'],
      ['Availability',  'availability'],
      ['Plan',          'plan'],
      ['How heard',     'source'],
      ['Notes',         'notes'],
    ],
  },
];

/* ── detail panel ── */
function DetailPanel({ sub, onClose, onStatusChange }) {
  const [status, setStatus] = useState(sub.status || 'new');
  const [saving, setSaving] = useState(false);

  const handleStatus = async (s) => {
    setStatus(s);
    setSaving(true);
    try { await updateSubmissionStatus(sub.id, s); }
    catch (e) { console.error(e); }
    finally { setSaving(false); }
    onStatusChange(sub.id, s);
  };

  return (
    <div className="sv-detail">
      <div className="sv-detail__head">
        <div>
          <div className="sv-detail__biz">{fmt(sub.bizname)}</div>
          <div className="sv-detail__meta">{fmt(sub.name)} · {fmtDate(sub.submittedAt)}</div>
        </div>
        <div className="sv-detail__head-actions">
        <button className="sv-btn-pdf" onClick={() => exportPDF(sub)} title="Export as PDF">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
          Export PDF
        </button>
        <button className="sv-detail__close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      </div>

      {/* status selector */}
      <div className="sv-detail__status-row">
        <span className="sv-detail__status-label">Status{saving ? ' (saving…)' : ''}</span>
        <div className="sv-status-btns">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => handleStatus(s)}
              className={`sv-status-btn sv-status-btn--${STATUS_COLOR[s]} ${status === s ? 'sv-status-btn--active' : ''}`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="sv-detail__body">
        {DETAIL_SECTIONS.map(sec => (
          <div key={sec.heading} className="sv-detail__sec">
            <div className="sv-detail__sec-heading">{sec.heading}</div>
            {sec.fields.map(([label, key]) => {
              const val = fmt(sub[key]);
              if (val === '—') return null;
              return (
                <div key={key} className="sv-detail__row">
                  <span className="sv-detail__row-key">{label}</span>
                  <span className="sv-detail__row-val">{val}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── main component ── */
export default function SubmissionsViewer() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    getSubmissions()
      .then(setSubs)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleStatusChange = (id, status) => {
    setSubs(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
  };

  const visible = filter === 'all' ? subs : subs.filter(s => (s.status || 'new') === filter);

  const counts = { all: subs.length };
  STATUS_OPTIONS.forEach(s => {
    counts[s] = subs.filter(x => (x.status || 'new') === s).length;
  });

  return (
    <div className="adm-page sv-page">
      <AdminTopbar title="Client Submissions" />

      <div className="sv-layout">

        {/* ── list panel ── */}
        <div className={`sv-list-panel ${selected ? 'sv-list-panel--narrow' : ''}`}>

          {/* filter tabs */}
          <div className="sv-filters">
            {['all', ...STATUS_OPTIONS].map(f => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`sv-filter-btn ${filter === f ? 'sv-filter-btn--active' : ''}`}
              >
                {f === 'all' ? 'All' : STATUS_LABEL[f]}
                <span className="sv-filter-count">{counts[f] || 0}</span>
              </button>
            ))}
          </div>

          {loading && (
            <div className="sv-empty">Loading submissions…</div>
          )}
          {!loading && visible.length === 0 && (
            <div className="sv-empty">
              {filter === 'all' ? 'No submissions yet.' : `No "${STATUS_LABEL[filter]}" submissions.`}
            </div>
          )}

          <div className="sv-list">
            {visible.map(sub => {
              const st = sub.status || 'new';
              const isActive = selected?.id === sub.id;
              return (
                <button
                  key={sub.id}
                  type="button"
                  className={`sv-row ${isActive ? 'sv-row--active' : ''}`}
                  onClick={() => setSelected(isActive ? null : sub)}
                >
                  <div className="sv-row__left">
                    <div className="sv-row__avatar">
                      {(sub.bizname || sub.name || '?')[0].toUpperCase()}
                    </div>
                    <div className="sv-row__info">
                      <div className="sv-row__biz">{fmt(sub.bizname) !== '—' ? fmt(sub.bizname) : fmt(sub.name)}</div>
                      <div className="sv-row__meta">
                        {fmt(sub.name)}
                        {sub.plan && sub.plan !== '—' ? ` · ${sub.plan.split('—')[0].trim()}` : ''}
                      </div>
                      <div className="sv-row__date">{fmtDate(sub.submittedAt)}</div>
                    </div>
                  </div>
                  <div className="sv-row__right">
                    <span className={`sv-badge sv-badge--${STATUS_COLOR[st]}`}>
                      {STATUS_LABEL[st]}
                    </span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                      className="sv-row__chevron">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── detail panel ── */}
        {selected && (
          <DetailPanel
            sub={selected}
            onClose={() => setSelected(null)}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>
    </div>
  );
}
